import React, { useState, useEffect, useMemo, useRef } from "react";
import { Autocomplete, TextField, Box } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { debounce } from "lodash";
import { TaskUser } from "../types";
import { UserDisplay } from "./UserAvatar";

const useStyles = makeStyles(() => ({
    root: {
        "& .MuiAutocomplete-inputRoot": {
            borderRadius: "10px",
        },
        "& legend": { display: "none" },
        "& fieldset": { top: 0 },
        "& .MuiInputLabel-root": {
            display: "none",
        },
    },
}));

interface DebouncedUserAutocompleteProps {
    value: TaskUser | null;
    onChange: (user: TaskUser | null) => void;
    onSearch: (query: string) => Promise<TaskUser[]>;
    label?: string;
    placeholder?: string;
    debounceDelay?: number;
    error?: boolean;
    helperText?: string;
    className?: string;
}

export const DebouncedUserAutocomplete: React.FC<
    DebouncedUserAutocompleteProps
> = ({
    value,
    onChange,
    onSearch,
    label,
    placeholder = "Search and select user...",
    debounceDelay = 300,
    error = false,
    helperText,
    className = "",
}) => {
    const classes = useStyles();

    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [options, setOptions] = useState<TaskUser[]>([]);
    const [loading, setLoading] = useState(false);

    // Keep latest onSearch reference
    const onSearchRef = useRef(onSearch);
    useEffect(() => {
        onSearchRef.current = onSearch;
    }, [onSearch]);

    const debouncedSetSearchQuery = useMemo(
        () =>
            debounce((value: string) => {
                setSearchQuery(value);
            }, debounceDelay),
        [debounceDelay]
    );

    useEffect(() => {
        debouncedSetSearchQuery(inputValue);
        return () => debouncedSetSearchQuery.cancel();
    }, [inputValue, debouncedSetSearchQuery]);

    useEffect(() => {
        let active = true;

        const fetchUsers = async () => {
            if (!open) return;

            setLoading(true);
            try {
                const result = await onSearchRef.current(searchQuery);
                if (active) {
                    setOptions(result || []);
                }
            } catch (err) {
                console.error("User search failed", err);
                if (active) setOptions([]);
            } finally {
                if (active) setLoading(false);
            }
        };

        fetchUsers();

        return () => {
            active = false;
        };
    }, [open, searchQuery]);

    useEffect(() => {
        if (value && !options.some((o) => o.id === value.id)) {
            setOptions((prev) => [value, ...prev]);
        }
    }, [value, options]);

    const getOptionLabel = (option: TaskUser) => option?.name || "";

    const isOptionEqualToValue = (option: TaskUser, value: TaskUser) =>
        option.id === value.id;

    return (
        <div className={`flex flex-col w-full ${className}`}>
            {label && (
                <label className="block text-sm font-medium text-slate-700 mb-2">
                    {label}
                </label>
            )}

            <Autocomplete
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                options={options}
                value={value}
                inputValue={inputValue}
                loading={loading}
                fullWidth
                className={classes.root}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={isOptionEqualToValue}
                filterOptions={(x) => x} // disable MUI filtering
                clearOnBlur={false}
                onInputChange={(_, newInputValue) => {
                    setInputValue(newInputValue);
                }}
                onChange={(_, newValue) => {
                    onChange(newValue);
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={placeholder}
                        error={error}
                        helperText={helperText}
                        InputProps={{
                            ...params.InputProps,
                        }}
                    />
                )}
                renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    return (
                        <Box
                            component="li"
                            key={option.id || key}
                            {...rest}
                            sx={{ py: 1 }}
                        >
                            <UserDisplay
                                user={option}
                                avatarSize="xs"
                                nameClassName="font-medium"
                                phoneClassName="text-slate-500"
                            />
                        </Box>
                    );
                }}
            />
        </div>
    );
};
